import React, { useEffect, useState, useRef } from "react";
import { FaTrash, FaArrowLeft, FaPlus, FaSearch, FaVolumeUp } from "react-icons/fa";
import { FaPencil } from "react-icons/fa6";
import ApiService from "../models/ApiService";

function Vocabulary() {
  const [vocabularyList, setVocabularyList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newWord, setNewWord] = useState({
    category: "",
    vocabulary_id: "",
    word: "",
    transliteration: "",
    devanagari_transliteration: "",
    english_name: "",
    hindi_meaning: "",
    voice_note: "",
    meaning: "",
  });
  const [audioFile, setAudioFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const audioRef = useRef(null);


  // Filter data based on selected category and search term
  const filteredData = selectedCategory
    ? vocabularyList
      .filter((item) => item.category === selectedCategory.vocabulary_category)
      .filter((item) =>
        item.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.transliteration.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.meaning.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.devanagari_transliteration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.english_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.hindi_meaning?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.voice_note_url?.toLowerCase()
      )
    : [];


  // Filter categories based on search term when in category view
  const filteredCategories = categories.filter(category =>
    category.vocabulary_category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewWord((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is an audio file
      if (!file.type.startsWith('audio/')) {
        setError("Please select an audio file (MP3, WAV, etc.)");
        return;
      }

      // Check file size (limit to 5MB)
      if (file.size > 10 * 1024 * 1024) {
        setError("File size should be less than 10MB");
        return;
      }

      setAudioFile(file);
      setError(null);
    }
  };

  useEffect(() => {
    fetchVocabulary();
    fetchVocabularyCategories();

    // Clean up audio on unmount
    audioRef.current = new Audio();
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const fetchVocabulary = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await ApiService.GetVocabulary();
      console.log("API Response:", res);

      const flatData = res.data.responseContent.reduce((acc, categoryGroup) => {
        return [
          ...acc,
          ...categoryGroup.data.map((item) => ({
            ...item,
            category: categoryGroup.category,
          })),
        ];
      }, []);

      setVocabularyList(flatData || []);
    } catch (error) {
      console.error("Failed to fetch vocabulary:", error);
      setError("Failed to load vocabulary. Please try again later.");
      setVocabularyList([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVocabularyCategories = async () => {
    try {
      const res = await ApiService.GetVocabularyCategory();
      console.log("Categories response:", res);
      if (res.data && res.data.responseContent) {
        setCategories(res.data.responseContent);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      setError("Failed to load categories. Please try again later.");
    }
  };

  const uploadAudioFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'your_upload_preset'); // Replace with your Cloudinary upload preset
    formData.append('folder', 'UrduUstaad/voice_notes');

    try {
      const response = await fetch('https://api.cloudinary.com/v1_1/your_cloud_name/video/upload', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error('Error uploading audio:', error);
      throw new Error('Failed to upload audio');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setIsUploading(true);

    try {

      const data = new FormData()
      data.append("category", newWord.category)
      data.append("vocabulary_id", newWord.vocabulary_id)
      data.append("word", newWord.word)
      data.append("transliteration", newWord.transliteration)
      data.append("devanagari_transliteration", newWord.devanagari_transliteration)
      data.append("english_name", newWord.english_name)
      data.append("hindi_meaning", newWord.hindi_meaning)
      data.append("meaning", newWord.meaning)


      if (audioFile) {
        data.append("voice_note", audioFile)
      }









      if (editingId !== null) {
        await ApiService.UpdateVocabulary(data, editingId);
        fetchVocabulary();
      } else {
        await ApiService.AddVocabulary(data);
        fetchVocabulary();
      }

      setShowModal(false);
      setEditingId(null);
      setAudioFile(null);
      setNewWord({
        category: "",
        vocabulary_id: "",
        word: "",
        transliteration: "",
        devanagari_transliteration: "",
        english_name: "",
        hindi_meaning: "",
        voice_note: "",
        meaning: "",
      });
    } catch (error) {
      console.error("Failed to save vocabulary:", error);
      setError("Failed to save. Please try again.");
    } finally {
      setIsLoading(false);
      setIsUploading(false);
    }
  };

  const handleEdit = (vocabularyItem) => {
    setEditingId(vocabularyItem.no);
    setNewWord({
      category: vocabularyItem.category,
      word: vocabularyItem.word,
      transliteration: vocabularyItem.transliteration,
      devanagari_transliteration: vocabularyItem.devanagari_transliteration || "",
      english_name: vocabularyItem.english_name || "",
      hindi_meaning: vocabularyItem.hindi_meaning || "",
      meaning: vocabularyItem.meaning || "",
      voice_note: vocabularyItem.voice_note || "",
      vocabulary_id: vocabularyItem.vocabulary_category_id,
    });
    setAudioFile(null);
    setShowModal(true);
  };

  const handleDelete = async (vocabularyItem) => {
    if (window.confirm("Are you sure you want to delete this word?")) {
      setIsLoading(true);
      try {
        await ApiService.DeleteVocabulary(vocabularyItem.no);
        fetchVocabulary();
      } catch (error) {
        console.error("Failed to delete vocabulary:", error);
        setError("Failed to delete. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };


  const playAudio = async (audioUrl) => {
    if (!audioUrl || !audioRef.current) return;

    const audio = audioRef.current;

    try {
      audio.src = audioUrl;
      audio.load();
      await audio.play();
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Audio error:", err);
        setError("Could not play audio");
      }
    }
  };



  const getCategoryWordCount = (categoryName) => {
    return vocabularyList.filter(item => item.category === categoryName).length;
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSearchTerm("");
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSearchTerm("");
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="p-4" id="primary-fonts">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[--text-color] mb-4">
          Vocabulary
        </h1>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Back button when a category is selected */}
        {selectedCategory && (
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={handleBackToCategories}
              className="flex items-center gap-2 text-[--primary-btn-color] hover:text-[--secondary-btn-color] transition-colors"
            >
              <FaArrowLeft className="text-[--text-color-g]" />
              Back to Categories
            </button>
            <h2 className="text-xl font-semibold text-[--text-color-g]">
              {selectedCategory.vocabulary_category}
            </h2>
          </div>
        )}

        {/* Search and Add Button Row */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          {/* Search Bar */}
          <div className="flex-1 max-w-md w-full">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={
                  selectedCategory
                    ? `Search in ${selectedCategory.vocabulary_category}...`
                    : "Search categories..."
                }
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-10 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-500 mt-2">
                {selectedCategory
                  ? `Found ${filteredData.length} word(s) matching "${searchTerm}"`
                  : `Found ${filteredCategories.length} category(s) matching "${searchTerm}"`
                }
              </p>
            )}
          </div>

          {/* Add New Vocabulary Button - Always Visible */}
          <div className="flex-shrink-0">
            <button
              onClick={() => {
                setEditingId(null);
                setNewWord({
                  category: selectedCategory?.vocabulary_category || "",
                  vocabulary_id: selectedCategory?.id || "",
                  word: "",
                  transliteration: "",
                  devanagari_transliteration: "",
                  english_name: "",
                  hindi_meaning: "",
                  voice_note: "",
                  meaning: "",
                });
                setAudioFile(null);
                setShowModal(true);
              }}
              className="bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition duration-200 w-full md:w-auto"
              disabled={isLoading}
            >
              <FaPlus />
              Add New Word
            </button>
          </div>
        </div>
      </div>

      {isLoading && vocabularyList.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[--border-color]"></div>
        </div>
      ) : (
        <>
          {/* Category Cards View */}
          {!selectedCategory && (
            <>
              {filteredCategories.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredCategories.map((category, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer border border-[--border-color]"
                      onClick={() => handleCategorySelect(category)}
                    >
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-[--text-color-g] mb-2">
                          {category.vocabulary_category}
                        </h3>
                        <p className="text-gray-600">
                          {getCategoryWordCount(category.vocabulary_category)} words
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-4">
                    {searchTerm
                      ? `No categories found matching "${searchTerm}"`
                      : "No categories available"
                    }
                  </div>
                  {searchTerm && (
                    <button
                      onClick={clearSearch}
                      className="text-[--primary-btn-color] hover:text-[--secondary-btn-color] underline"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Vocabulary Cards View */}
          {selectedCategory && (
            <>
              {filteredData.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredData.map((item, index) => (
                    <div
                      key={item._id}
                      className="bg-white rounded-lg shadow-md border border-[--border-color] hover:shadow-lg transition-shadow duration-300"
                    >
                      <div className="p-6">
                        {/* Word and Audio */}
                        <div className="mb-4 flex justify-between items-start">
                          <button
                            onClick={() => playAudio(item.voice_note_url)}
                            className={`p-2 rounded-full ${item.voice_note_url
                              ? "text-[--primary-btn-color] hover:bg-blue-50"
                              : "text-gray-300 cursor-not-allowed"
                              }`}
                            // disabled={!item.voice_note}
                            title={item.voice_note_url ? "Play pronunciation" : "No audio available"}
                          >
                            <FaVolumeUp className="text-xl" />
                          </button>
                          <div>
                            <h3
                              className="text-2xl font-bold text-[--text-color-g] text-right mb-4"
                              id="primary-urdu-fonts"
                            >
                              {item.word}
                            </h3>
                            <p className="text-sm text-[--text-color] font-bold text-right">
                              {item.transliteration}
                            </p>
                            {item.devanagari_transliteration && (
                              <p className="text-sm text-[--text-color] font-bold text-right mt-1">
                                {item.devanagari_transliteration}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Meaning */}
                        <div className="mb-4">
                          <p className="text-[--text-color-g] font-medium mb-2">
                            {item.meaning}
                          </p>
                          {item.hindi_meaning && (
                            <p className="text-sm text-gray-600">
                              <strong>Hindi:</strong> {item.hindi_meaning}
                            </p>
                          )}
                          {item.english_name && (
                            <p className="text-sm text-gray-600">
                              <strong>English:</strong> {item.english_name}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 text-[--primary-btn-color] rounded-full hover:bg-gray-100 transition-colors"
                            title="Edit"
                            disabled={isLoading}
                          >
                            <FaPencil />
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className="p-2 text-red-600 rounded-full hover:bg-gray-100 transition-colors"
                            title="Delete"
                            disabled={isLoading}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="col-span-full text-center py-12">
                  <div className="text-gray-500 text-lg mb-4">
                    {searchTerm
                      ? `No words found matching "${searchTerm}" in ${selectedCategory.vocabulary_category}`
                      : `No vocabulary items found in ${selectedCategory.vocabulary_category}`
                    }
                  </div>
                  <div className="flex gap-4 justify-center">
                    {searchTerm && (
                      <button
                        onClick={clearSearch}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Clear Search
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setNewWord({
                          category: selectedCategory.vocabulary_category,
                          vocabulary_id: selectedCategory.id,
                          word: "",
                          transliteration: "",
                          devanagari_transliteration: "",
                          english_name: "",
                          hindi_meaning: "",
                          voice_note: "",
                          meaning: "",
                        });
                        setAudioFile(null);
                        setShowModal(true);
                      }}
                      className="bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white px-6 py-2 rounded-lg flex items-center gap-2 transition duration-200"
                    >
                      <FaPlus />
                      Add New Word
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Add/Edit Vocabulary Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-screen-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl text-[--text-color-g] font-bold mb-4">
                {editingId !== null ? "Edit Vocabulary" : "Add New Vocabulary"}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    value={newWord.category}
                    onChange={(e) => {
                      const selectedCat = categories.find(
                        (cat) => cat.vocabulary_category === e.target.value
                      );
                      setNewWord((prev) => ({
                        ...prev,
                        category: e.target.value,
                        vocabulary_id: selectedCat?.id || "",
                      }));
                    }}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    required
                    disabled={isLoading}
                  >
                    <option value="">Select a category</option>
                    {categories.map((category, index) => (
                      <option key={index} value={category.vocabulary_category}>
                        {category.vocabulary_category}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Word
                  </label>
                  <input
                    type="text"
                    name="word"
                    value={newWord.word}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Transliteration
                  </label>
                  <input
                    type="text"
                    name="transliteration"
                    value={newWord.transliteration}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Devanagari Transliteration
                  </label>
                  <input
                    type="text"
                    name="devanagari_transliteration"
                    value={newWord.devanagari_transliteration}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    disabled={isLoading}
                    placeholder="Optional"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    English Name
                  </label>
                  <input
                    type="text"
                    name="english_name"
                    value={newWord.english_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    disabled={isLoading}
                    placeholder="Optional"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Hindi Meaning
                  </label>
                  <input
                    type="text"
                    name="hindi_meaning"
                    value={newWord.hindi_meaning}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    disabled={isLoading}
                    placeholder="Optional"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[--text-color] mb-2">
                    Meaning
                  </label>
                  <input
                    type="text"
                    name="meaning"
                    value={newWord.meaning}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                    // required
                    disabled={isLoading}
                  />
                </div>

                {/* Audio Upload Section */}
                <div className="mb-6">
                  <label className="block text-[--text-color] mb-2">
                    Pronunciation Audio
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100"
                        disabled={isLoading || isUploading}
                      />
                    </div>

                    {audioFile && (
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-green-700 text-sm">
                          Selected: {audioFile.name}
                        </p>
                      </div>
                    )}

                    {newWord.voice_note && !audioFile && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-blue-700 text-sm mb-2">
                          Existing audio file:
                        </p>
                        <button
                          type="button"
                          onClick={() => playAudio(newWord.voice_note)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                        >
                          <FaVolumeUp />
                          Test current audio
                        </button>
                      </div>
                    )}

                    <p className="text-xs text-gray-500">
                      Upload an MP3 or WAV file (max 5MB) for pronunciation
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingId(null);
                      setAudioFile(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={isLoading || isUploading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[--primary-btn-color] rounded-lg text-white hover:bg-[--secondary-btn-color] flex items-center justify-center gap-2 disabled:opacity-50"
                    disabled={isLoading || isUploading}
                  >
                    {isLoading || isUploading ? (
                      <>
                        <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        {isUploading ? "Uploading..." : "Saving..."}
                      </>
                    ) : (
                      editingId !== null ? "Update Word" : "Add Word"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Vocabulary;